import dayjs from 'dayjs';
import Typo, { TypoProps } from '../../atom/Typo';
import Badge, { badgeColorData, textColorData } from '../../atom/Badge';
import ImagePreview from '../../molecule/ImagePreview';

// BOLD_TEXT
interface DetailSecitonBoldTextRendererProps {
  typoType?: TypoProps['typoType'];
  typoColor?: TypoProps['typoColor'];
}
const DetailSecitonBoldTextRenderer =
  (props?: DetailSecitonBoldTextRendererProps) => (value: string) => (
    <Typo
      typoType={props?.typoType || 'h20sb'}
      typoColor={props?.typoColor || 'blue9'}
    >
      {value}
    </Typo>
  );

// DATE
interface DetailSecitonDateRendererProps {
  format?: string;
}
const DetailSecitonDateRenderer =
  (props?: DetailSecitonDateRendererProps) => (value: string) => {
    const dateValue = dayjs(value);

    return dateValue.isValid()
      ? dateValue.format(props?.format || 'YYYY-MM-DD')
      : '-';
  };

// Y_OR_N
interface DetailSecitonYNRendererProps {
  trueLabel?: string;
  falseLabel?: string;
}
const DetailSecitonYNRenderer =
  (props?: DetailSecitonYNRendererProps) => (value: string) => {
    return value ? props?.trueLabel || 'Y' : props?.falseLabel || 'N';
  };

// IMAGE
const DetailSecitonImageRenderer =
  (props?: { width: number; height: number }) => (value: string) => {
    if (!value || (!value.includes('http://') && !value.includes('https://')))
      return '-';

    return (
      <ImagePreview
        imgSrc={value}
        width={props?.width}
        height={props?.height || props?.width}
        isPreviewRatioFixed={false}
      />
    );
  };

// FORMATTER
interface DetailSecitonFormatterRendererProps {
  formatter: (target: string) => string;
}
const DetailSecitonFormatterRenderer =
  ({ formatter }: DetailSecitonFormatterRendererProps) =>
  (value: string) => {
    return formatter(value);
  };

// BADGE
interface DetailSecitonBadgeRendererProps {
  options: {
    key: string;
    label: string;
    badgeColor?: keyof typeof badgeColorData;
    textBadgeColor?: keyof typeof textColorData;
  }[];
}
const DetailSecitonBadgeRenderer =
  ({ options }: DetailSecitonBadgeRendererProps) =>
  (value: string) => {
    const target = options.find(item => item.key === value);

    const result = {
      value: target ? target.label : value,
      badgeColor: target?.badgeColor || 'gray',
      textBadgeColor: target?.textBadgeColor || 'gray',
    };

    return (
      <Badge
        badgeColor={result.badgeColor}
        textBadgeColor={result.textBadgeColor}
      >
        {target ? target.label : value}
      </Badge>
    );
  };

interface DetailSecitonPrefixRendererProps {
  prefix?: string;
  suffix?: string;
}
const DetailSecitonPrefixRenderer =
  ({ prefix, suffix }: DetailSecitonPrefixRendererProps) =>
  (value: string) => {
    const result = prefix ? `${prefix}${value}` : value;
    return suffix ? `${result}${suffix}` : result;
  };

export const DetailSctionRenderer = {
  TEXT: () => (value: string) => value,
  NUMBER: () => (value: string | number) => {
    return isNaN(Number(value)) ? '0' : Number(value).toLocaleString();
  },
  BOLD_TEXT: DetailSecitonBoldTextRenderer,
  DATE: DetailSecitonDateRenderer,
  Y_OR_N: DetailSecitonYNRenderer,
  IMAGE: DetailSecitonImageRenderer,
  FORMATTER: DetailSecitonFormatterRenderer,
  BADGE: DetailSecitonBadgeRenderer,
  PREFIX_SUFFIX: DetailSecitonPrefixRenderer,
};
